import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import {
    DatepickerComponent,
    DateUtilService,
    LoadingOverlayComponent,
    MAT_ICON,
    PaginationComponent,
    SelectComponent,
    SelectOption,
    SessionPreferenceService,
    TOAST_STATE,
    ToastmessageService,
    TranslationPipe,
    TranslationService
} from '@prekog/common-ui';
import { Observable } from 'rxjs';

import { FUTURE_DATE_ERROR } from '../../shared/adminconstants';
import { ReferenceDataService } from '../../shared/reference-data.service';
import { UtilService } from '../../shared/util.service';
import { StatisticsFilter } from '../statisticsfilter.interface';
import { GetPartnerStatisticRequest } from './getpartnerstatistic.request';
import { GetPartnerStatisticResponse } from './getpartnerstatistic.response';
import { PartnerStatisticTotals } from '../../shared/models/partner-statistic-totals.model';
import { CalculatePartnerStatisticTotalsUseCase } from '../../use-cases/calculatepartnerstatistictotals/calculate-partner-statistic-totals.usecase';
import { GetPartnerStatisticTotalsRequest } from '../../use-cases/loadpartnerglobaltotals/get-partner-statistic-totals.request';
import { LoadPartnerGlobalTotalsUseCase } from '../../use-cases/loadpartnerglobaltotals/load-partner-global-totals.usecase';
import { LoadPartnerStatisticUseCase } from '../../use-cases/loadpartnerstatistic/load-partner-statistic.usecase';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-partnerstatistic',
    imports: [
        MatIconModule,
        DatepickerComponent,
        LoadingOverlayComponent,
        PaginationComponent,
        SelectComponent,
        TranslationPipe,
        ReactiveFormsModule,
        CommonModule,
        RouterModule
    ],
    templateUrl: './partnerstatistic.component.html',
    styleUrl: './partnerstatistic.component.scss'
})
export class PartnerStatisticComponent implements OnInit {
    defaultEndDate!: string;
    defaultStartDate!: string;
    endDate = new Date();
    maxDate!: string;
    readonly page = signal(0);
    partnerOptions: SelectOption[] = [];
    partnerStatisticsList!: GetPartnerStatisticResponse;
    productStatisticsFilter!: FormGroup;
    readonly savedEndDate = signal<string>('');
    readonly savedPartnerIds = signal<number[]>([]);
    readonly savedStartDate = signal<string>('');
    readonly selectedRowIndex = signal<number | null>(null);
    readonly size = signal(25);
    startDate = new Date();
    readonly totalItems = signal(0);
    readonly totals = signal<PartnerStatisticTotals | undefined>(undefined);
    readonly globalTotals = signal<PartnerStatisticTotals | undefined>(undefined);

    private _loadUseCase = inject(LoadPartnerStatisticUseCase);
    private _loadGlobalTotalsUseCase = inject(LoadPartnerGlobalTotalsUseCase);
    private _calculateTotalsUseCase = inject(CalculatePartnerStatisticTotalsUseCase);
    private _cdr = inject(ChangeDetectorRef);
    private _dateUtilService = inject(DateUtilService);
    private _fb = inject(FormBuilder);
    private _referenceData = inject(ReferenceDataService);
    private _router = inject(Router);
    private _sessionPreference = inject(SessionPreferenceService);
    private _toastMessageService = inject(ToastmessageService);
    private _translateService = inject(TranslationService);
    private _utilService = inject(UtilService);

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            void this._router.navigateByUrl('/');
        } else {
            const dateDefaults = this._dateUtilService.getDefaultDateFilterValues('2023-01-01');
            this.defaultStartDate = dateDefaults.defaultStartDate;
            this.defaultEndDate = dateDefaults.defaultEndDate;
            this.startDate = dateDefaults.startDate;
            this.endDate = dateDefaults.endDate;
            this.maxDate = this._dateUtilService.getMaxDateForDateSelection(true);
            this.savedStartDate.set(this.defaultStartDate);
            this.savedEndDate.set(this.defaultEndDate);
            this._setDefaultFilters();
            const savedFilters = this._sessionPreference.load<StatisticsFilter>('partnerstatistic-filters');
            if (savedFilters) {
                this.productStatisticsFilter.patchValue(savedFilters);
                this.savedPartnerIds.set(savedFilters.partnerIds ?? []);
                this.savedStartDate.set(savedFilters.startDate ? String(savedFilters.startDate) : '');
                this.savedEndDate.set(savedFilters.endDate ? String(savedFilters.endDate) : '');
            }
            this._loadPartners();
            this.updateStockingStatisticsByFilter();
        }
    }

    onPartnerIdsChange(selectedIds: (number | string)[]): void {
        this.productStatisticsFilter.patchValue({ partnerIds: selectedIds });
    }

    onStartDateChange(date: string): void {
        this.productStatisticsFilter.patchValue({ startDate: date });
    }

    onEndDateChange(date: string): void {
        this.productStatisticsFilter.patchValue({ endDate: date });
    }

    updateStockingStatisticsByFilter(): void {
        this.page.set(0);
        this._sessionPreference.save('partnerstatistic-filters', this.productStatisticsFilter.value);
        const request = this._createGetStatisticsRequest();
        this._loadStatistics(request, this.selectedRowIndex() !== null);
        this.selectedRowIndex.set(null);
        this._loadGlobalTotals();
    }

    onResetFilters(): void {
        this._sessionPreference.remove('partnerstatistic-filters');
        this._setDefaultFilters();
        this.page.set(0);
        this.size.set(25);
        this.savedPartnerIds.set([]);
        this.savedStartDate.set(this.defaultStartDate);
        this.savedEndDate.set(this.defaultEndDate);
        this.updateStockingStatisticsByFilter();
    }

    selectRow(index: number): void {
        const statistics = this.partnerStatisticsList?.partnerStatisticsList;
        if (!statistics) {
            return;
        }

        if (this.selectedRowIndex() === index) {
            statistics[index].expanded = false;
            this.selectedRowIndex.set(null);
        } else {
            const previousIndex = this.selectedRowIndex();
            if (previousIndex !== null && statistics[previousIndex]) {
                statistics[previousIndex].expanded = false;
            }
            statistics[index].expanded = true;
            this.selectedRowIndex.set(index);
        }

        this._refreshTotals();
    }

    private _refreshTotals(): void {
        const selectedIndex = this.selectedRowIndex();
        const statistics = this.partnerStatisticsList?.partnerStatisticsList;

        if (selectedIndex !== null && statistics?.[selectedIndex]) {
            const rowTotals = this._calculateTotalsUseCase.execute({ list: statistics, selectedIndex });
            this.totals.set(rowTotals as PartnerStatisticTotals);
        } else {
            this.totals.set(this.globalTotals());
        }
    }

    onPageChanged(event: { page: number; size: number }): void {
        this.page.set(event.page);
        this.size.set(event.size);
        const request = this._createGetStatisticsRequest();
        this._loadStatistics(request, this.selectedRowIndex() !== null);
        this.selectedRowIndex.set(null);
    }

    private _createGetStatisticsRequest(): GetPartnerStatisticRequest {
        const filterValues = this.productStatisticsFilter.value as StatisticsFilter;

        return {
            partnerIds: this._utilService.nullIfEmpty(filterValues.partnerIds),
            fromDate: filterValues.startDate,
            toDate: filterValues.endDate,
            page: this.page(),
            size: this.size()
        };
    }

    private _showFutureDateError(): void {
        this._toastMessageService.displayToastMessage(
            TOAST_STATE.error,
            this._translateService.translate(FUTURE_DATE_ERROR),
            MAT_ICON.error
        );
    }

    private _setDefaultFilters(): void {
        const futureDateValidator = this._dateUtilService.futureDateValidator(() => this._showFutureDateError());

        this.productStatisticsFilter = this._fb.group({
            partnerIds: [[]],
            startDate: [this.defaultStartDate, [Validators.required, futureDateValidator]],
            endDate: [this.defaultEndDate, [Validators.required, futureDateValidator]]
        });
    }

    private _loadStatistics(request: GetPartnerStatisticRequest, refreshGlobalTotals: boolean): void {
        (this._loadUseCase.execute(request) as Observable<GetPartnerStatisticResponse>).subscribe((response) => {
            this.partnerStatisticsList = response;
            this.totalItems.set(response.totalItems ?? 0);
            this.selectedRowIndex.set(null);
            if (!refreshGlobalTotals) {
                this._refreshTotals();
            }
            this._cdr.markForCheck();
        });
    }

    private _loadGlobalTotals(): void {
        const filterValues = this.productStatisticsFilter.value as StatisticsFilter;
        const totalsRequest: GetPartnerStatisticTotalsRequest = {
            fromDate: filterValues.startDate,
            toDate: filterValues.endDate,
            partnerIds: this._utilService.nullIfEmpty(filterValues.partnerIds)
        };
        (this._loadGlobalTotalsUseCase.execute(totalsRequest) as Observable<PartnerStatisticTotals>).subscribe(
            (totals) => {
                this.globalTotals.set(totals);
                this._refreshTotals();
                this._cdr.markForCheck();
            }
        );
    }

    private _loadPartners(): void {
        this._referenceData.loadPartners().subscribe((partners) => {
            this.partnerOptions = partners.map((partner) => ({
                id: partner.id!,
                name: partner.name!
            }));
            this._cdr.markForCheck();
        });
    }
}
